import _ from 'lodash'
import {
	CompGridData,
	CompsGridDataMap,
	DataForMesh,
	FlatMeshData,
	GridOverflow,
	Mesh,
	MeshDataOptions,
	MeshHierarchyFlat,
	MeshHierarchyStructure,
	MeshMarginsOptions,
	MeshOptions,
	MeshStyles,
	NearestTopTopPusher,
	NegativeBottomTops,
	VerticalArea,
} from '../Types'

import { getComponentBoundingBox } from './mesh3BoundingLayout'
import calculateCompsGridData from './mesh3CalculateCompsGridData'
import calculateGridPoints from './mesh3calculateGridPoints'
import { bottom, fitToContentHeight, getContainerHeight, height, isRotated, top } from './mesh3Utils'
import {
	getCompStyles,
	getEmptyContainerStyles,
	getGridContainerStyles,
	getGridWrapperStyle,
	getContainerStyle,
	getWedgeStyle,
	unitize,
	getRootContainerStyle,
	getCompAbsoluteStyle,
	getRotatedComponentStyle,
	getRotatedComponentWrapperStyle,
} from './mesh3CssUtils'
import { RowsLimitError } from '../structure2mesh/rowsAPIFactory'
import { MeshV2Params } from '@wix/thunderbolt-becky-root'
import { MeshItemLayout, SingleLayoutData } from '@wix/thunderbolt-becky-types'

// @ts-ignore
const getGridWrapperId = (containerId) => `${containerId}-gridWrapper`
// @ts-ignore
const getGridContainerId = (containerId) => `${containerId}-gridContainer`

const getGridVerticalArea = (
	containerSingleLayoutData: SingleLayoutData,
	compsMinTop: number,
	compsMaxBottom: number
): VerticalArea => {
	const areaTop = _.min([0, compsMinTop])
	const areaBottom = _.max([getContainerHeight(containerSingleLayoutData) || 0, compsMaxBottom])
	return {
		// @ts-ignore
		top: areaTop,
		// @ts-ignore
		bottom: areaBottom,
	}
}

const childrenContainerMinHeight = (
	containerSingleLayoutData: SingleLayoutData,
	childIdToSingleLayoutData: {
		[compId: string]: SingleLayoutData
	},
	mostBottom: number,
	gridOverflow: VerticalArea,
	{ minLockMargin }: MeshMarginsOptions
): string => {
	const overflowTop = _.get(gridOverflow, 'top', 0)

	if (fitToContentHeight(containerSingleLayoutData)) {
		return 'auto'
	}

	const isSoft = getContainerHeight(containerSingleLayoutData) - mostBottom > minLockMargin!

	if (_.isEmpty(childIdToSingleLayoutData) || isSoft) {
		return unitize(getContainerHeight(containerSingleLayoutData) + overflowTop)
	}

	return 'auto'
}

const getGridOverflow = (containerSingleLayoutData: SingleLayoutData, gridVerticalArea: VerticalArea): GridOverflow => {
	const overflowFromTop = 0 - gridVerticalArea.top
	const overflowFromBottom = fitToContentHeight(containerSingleLayoutData)
		? 0
		: // @ts-ignore
		  gridVerticalArea.bottom - getContainerHeight(containerSingleLayoutData)

	if (!overflowFromTop && !overflowFromBottom) {
		// @ts-ignore
		return null
	}

	return {
		top: overflowFromTop,
		bottom: overflowFromBottom,
	}
}

const getRootStructure: (meshHierarchy: MeshHierarchyFlat, containerId: string) => MeshHierarchyStructure = (
	meshHierarchy,
	containerId
) => {
	const getCompStructure: (compId: string, childrenIds: Array<string>) => MeshHierarchyStructure = (
		compId,
		childrenIds = []
	) => ({
		id: compId,
		children: _.map(childrenIds, (childId) => getCompStructure(childId, meshHierarchy[childId])),
	})

	return getCompStructure(containerId, meshHierarchy[containerId])
}
// @ts-ignore
const addChildToMesh = (containerMeshData: FlatMeshData, { style, id }): FlatMeshData => {
	containerMeshData.hierarchy[getGridContainerId(containerMeshData.id)].push(id)
	containerMeshData.styles[id] = style
	return containerMeshData
}

const addComponentsToMeshData = (
	containerSingleLayoutData: SingleLayoutData,
	childIdToSingleLayoutData: {
		[compId: string]: SingleLayoutData
	},
	containerMeshData: FlatMeshData,
	compsGridData: CompsGridDataMap,
	gridVerticalArea: VerticalArea,
	meshOptions: MeshDataOptions,
	// @ts-ignore
	negativeBottomTops
) => {
	// @ts-ignore
	const compsStyles = []
	_.keys(childIdToSingleLayoutData).forEach((childId) => {
		const childSingleLayout = childIdToSingleLayoutData[childId]
		if ((childSingleLayout.itemLayout as MeshItemLayout).isAbsolutePosition) {
			const style = getCompAbsoluteStyle(childSingleLayout, containerSingleLayoutData)
			compsStyles.push({
				id: childId,
				style,
			})
		} else if (isRotated(childSingleLayout)) {
			addRotatedComponentAndWrapper(
				// @ts-ignore
				compsStyles,
				containerSingleLayoutData,
				childId,
				childSingleLayout,
				containerMeshData,
				// @ts-ignore
				compsGridData[child.id],
				gridVerticalArea,
				meshOptions,
				negativeBottomTops
			)
		} else {
			const style = getCompStyles(
				childId,
				childIdToSingleLayoutData[childId],
				compsGridData[childId],
				gridVerticalArea,
				containerSingleLayoutData,
				meshOptions,
				negativeBottomTops
			)
			compsStyles.push({
				id: childId,
				style,
			})
		}
	})
	// @ts-ignore
	_.forEach(compsStyles, addChildToMesh.bind(null, containerMeshData))
}

const addRotatedComponentAndWrapper = (
	// @ts-ignore
	compsStyles,
	containerSingleLayoutData: SingleLayoutData,
	childId: string,
	childSingleLayoutData: SingleLayoutData,
	containerMeshData: FlatMeshData,
	compGridData: CompGridData,
	gridVerticalArea: VerticalArea,
	meshOptions: MeshDataOptions,
	// @ts-ignore
	negativeBottomTops
) => {
	const wrapperStyle = getRotatedComponentWrapperStyle(
		childId,
		childSingleLayoutData,
		containerSingleLayoutData,
		compGridData,
		gridVerticalArea,
		meshOptions,
		negativeBottomTops
	)
	const wrapperId = `${childId}-rotated-wrapper`
	const childStyle = getRotatedComponentStyle(childSingleLayoutData)
	compsStyles.push({
		id: wrapperId,
		style: wrapperStyle,
	})

	containerMeshData.hierarchy[wrapperId] = [childId]
	containerMeshData.styles[childId] = childStyle
}

const addWedgesToMeshData = (
	containerMeshData: FlatMeshData,
	compsGridData: CompsGridDataMap,
	{ cssGridVariant }: MeshDataOptions
) => {
	const ie = cssGridVariant === 'ms'

	_(compsGridData)
		.pickBy((compGridData) => compGridData.isWedge)
		.map((wedgeGridData: CompGridData) => ({
			id: `${containerMeshData.id}-${wedgeGridData.id}`,
			style: getWedgeStyle(wedgeGridData, ie),
		}))
		// @ts-ignore
		.reduce(addChildToMesh, containerMeshData)
}

const createContainerMeshData = (
	containerId: string,
	rootContainerId: string,
	containerSingleLayoutData: SingleLayoutData,
	numberOfRows: number,
	containerMinHeight: string,
	gridOverflow: GridOverflow,
	{ cssGridVariant }: MeshDataOptions
): FlatMeshData => {
	const gridWrapperId = getGridWrapperId(containerId)
	const gridContentId = getGridContainerId(containerId)
	const hierarchy: MeshHierarchyFlat = {
		// @ts-ignore
		[containerId]: [gridWrapperId],
		[gridWrapperId]: [gridContentId],
		[gridContentId]: [],
	}

	const rootContainerStyle = getRootContainerStyle(containerSingleLayoutData)
	const containerStyle = getContainerStyle(containerSingleLayoutData)
	const gridWrapperStyle = getGridWrapperStyle(gridOverflow, cssGridVariant === 'ms')
	const gridContainerStyles = getGridContainerStyles(
		// @ts-ignore
		cssGridVariant,
		numberOfRows,
		gridOverflow,
		containerMinHeight
	)

	const styles: MeshStyles = {
		[`${rootContainerId}-root`]: rootContainerStyle,
		[containerId]: containerStyle,
		[gridWrapperId]: gridWrapperStyle,
		[gridContentId]: gridContainerStyles,
	}

	return {
		id: containerId,
		hierarchy,
		styles,
	}
}

const createEmptyContainerMeshData = (
	containerId: string,
	containerSingleLayoutData: SingleLayoutData,
	containerMinHeight: string
): FlatMeshData => {
	const gridContentId = getGridContainerId(containerId)
	const hierarchy: MeshHierarchyFlat = {
		// @ts-ignore
		[containerId]: [gridContentId],
		[gridContentId]: [],
	}

	const containerStyle = getContainerStyle(containerSingleLayoutData)
	const gridContainerStyles = getEmptyContainerStyles(containerMinHeight)

	const styles: MeshStyles = {
		// @ts-ignore
		[containerId]: containerStyle,
		[gridContentId]: gridContainerStyles,
	}

	return {
		id: containerId,
		hierarchy,
		styles,
	}
}

const getCompsPushingData = (childIdToSingleLayoutData: {
	[compId: string]: SingleLayoutData
}): { negativeBottomTops: NegativeBottomTops; nearestTopTopPusher: NearestTopTopPusher } => {
	const compIds = _.keys(childIdToSingleLayoutData)
	return _.reduce(
		compIds,
		({ negativeBottomTops, nearestTopTopPusher }, compId) => {
			const singleLayout = childIdToSingleLayoutData[compId]
			const maxTopTopMap = {}
			const compTop = top(singleLayout)
			const compBottom = bottom(singleLayout)
			const compCenter = compTop + height(singleLayout) / 2

			_.forEach(compIds, (testedPushedCompId) => {
				const testedPushedComp = childIdToSingleLayoutData[testedPushedCompId]
				if (compId === testedPushedCompId) {
					return
				}

				const isPushedCompTopOverlappingComp = _.inRange(top(testedPushedComp), compTop + 1, compBottom)
				if (isPushedCompTopOverlappingComp) {
					const isSiblingBottomBelowCompBottom = bottom(testedPushedComp) > compBottom
					if (isSiblingBottomBelowCompBottom) {
						if (_.inRange(top(testedPushedComp), compCenter, compBottom)) {
							// bottom-top
							// @ts-ignore
							negativeBottomTops[compId] = _.max([
								compBottom - top(testedPushedComp),
								// @ts-ignore
								negativeBottomTops[compId],
							])
						}
					} else {
						// full overlapping between sibling comps
						// top-top
						if (
							height(testedPushedComp) > 0 &&
							// @ts-ignore
							(_.isUndefined(maxTopTopMap[testedPushedCompId]) ||
								// @ts-ignore
								compTop > maxTopTopMap[testedPushedCompId])
						) {
							// @ts-ignore
							maxTopTopMap[testedPushedCompId] = compTop
							// @ts-ignore
							nearestTopTopPusher[testedPushedCompId] = compId
						}
					}
				}
			})

			return { negativeBottomTops, nearestTopTopPusher }
		},
		{ negativeBottomTops: {}, nearestTopTopPusher: {} }
	)
}

const getGridDataForMesh = (
	containerSingleLayoutData: SingleLayoutData,
	childIdToSingleLayoutData: {
		[compId: string]: SingleLayoutData
	},
	marginsOptions: MeshMarginsOptions
): DataForMesh => {
	const childrenLayouts = _.values(childIdToSingleLayoutData)
	const compsMinTop = _(childrenLayouts).map(top).min()
	const compsMaxBottom = _(childrenLayouts).map(bottom).max()
	// @ts-ignore
	const verticalArea = getGridVerticalArea(containerSingleLayoutData, compsMinTop, compsMaxBottom)
	const gridOverflow = getGridOverflow(containerSingleLayoutData, verticalArea)

	const { nearestTopTopPusher, negativeBottomTops } = getCompsPushingData(childIdToSingleLayoutData)
	const sortedGridPointsData = calculateGridPoints(childIdToSingleLayoutData, negativeBottomTops)
	const compsGridData = calculateCompsGridData(
		sortedGridPointsData,
		verticalArea,
		nearestTopTopPusher,
		marginsOptions
	)
	// @ts-ignore
	const numberOfRows = _(compsGridData).map('rowEnd').max() - 1

	return {
		verticalArea,
		numberOfRows,
		compsGridData,
		negativeBottomTops,
		overflow: gridOverflow,
		containerMinHeight: childrenContainerMinHeight(
			containerSingleLayoutData,
			childIdToSingleLayoutData,
			// @ts-ignore
			compsMaxBottom,
			gridOverflow,
			marginsOptions
		),
	}
}

export function createMeshData(
	containerId: string,
	rootContainerId: string,
	containerSingleLayoutData: SingleLayoutData,
	childIdToSingleLayoutData: {
		[compId: string]: SingleLayoutData
	},
	meshOptions: MeshDataOptions
): FlatMeshData {
	const nonAbsChildren = _.reject(childIdToSingleLayoutData, 'itemLayout.isAbsolutePosition')
	const gridComponents = nonAbsChildren.map((comp) => {
		return isRotated(comp) ? _.assign({}, comp, getComponentBoundingBox(comp)) : comp
	})

	try {
		const {
			numberOfRows,
			verticalArea,
			containerMinHeight,
			overflow,
			negativeBottomTops,
			compsGridData,
		} = getGridDataForMesh(containerSingleLayoutData, childIdToSingleLayoutData, meshOptions)

		const containerMeshData = _.isEmpty(gridComponents)
			? createEmptyContainerMeshData(containerId, containerSingleLayoutData, containerMinHeight)
			: createContainerMeshData(
					containerId,
					rootContainerId,
					containerSingleLayoutData,
					numberOfRows,
					containerMinHeight,
					overflow,
					meshOptions
			  )
		addComponentsToMeshData(
			containerSingleLayoutData,
			childIdToSingleLayoutData,
			containerMeshData,
			compsGridData,
			verticalArea,
			meshOptions,
			negativeBottomTops
		)
		addWedgesToMeshData(containerMeshData, compsGridData, meshOptions)

		return containerMeshData
	} catch (e) {
		if (e instanceof RowsLimitError) {
			// support 1000 rows
			// return getAbsoluteContainerMeshData(container)
		}
		throw e
	}
}

export const mesh3 = (
	{
		id: containerId = 'root',
		rootId: rootContainerId = '',
		childIdToSingleLayoutData = {},
		containerSingleLayoutData,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		shouldRenderContentAreaIndicators,
	}: MeshV2Params,
	{ cssGridVariant = 'standard', softMargin = 10, minLockMargin = 70 }: MeshOptions = {}
): Mesh => {
	const { hierarchy, styles } = createMeshData(
		containerId,
		rootContainerId,
		containerSingleLayoutData,
		childIdToSingleLayoutData,
		{
			cssGridVariant,
			softMargin,
			minLockMargin,
		}
	)
	return {
		// @ts-ignore
		structure: getRootStructure(hierarchy, containerId),
		styles,
	}
}
